import * as yup from 'yup'

const createListSchema = fieldName =>
  yup
    .mixed()
    .transform(value => {
      // Convert a comma-separated string into an array if it's a string
      if (typeof value === 'string') {
        return value
          .split(',')
          .map(item => item.trim())
          .filter(Boolean) // Filter out empty strings from the array
      }
      return value // Return the value as is if it's already an array
    })
    .test(`is-valid-${fieldName}`, `Must be a string or an array of strings`, value => {
      // Allow empty string or empty array
      if (!value || value.length === 0) return true

      // Check if the value is a valid array of strings
      if (Array.isArray(value)) {
        return value.every(item => typeof item === 'string')
      }

      return typeof value === 'string'
    })

export const initialValues = {
  description: '',
  displayText: '',
  shortDisplayText: '',
  displayType: '',
  ruleGroup: '',
  rulePriority: 0,
  pointMultiplier: '',

  //Advanced options
  optIn: 'false', // Default value set
  singleTransaction: 'true',
  daysAfterActivation: '',
  startDate: '',
  endDate: '',
  rewardClassList: [],
  mccInclusionList: [],
  mccExclusionList: [],
  midInclusionList: [],
  midExclusionList: [],
  chainInclusionList: [],
  chainExclusionList: [],
  online: '',
  wallet: [],
  conditions: '',
  minimumCount: '',
  minimumSpend: '',
  maxCount: '',
  maxPoints: '',
  fixedPoints: '',
  pointsRequired: '',
  displayScore: '',
  eventType: 'CARD_TXN',
  ledgerType: 'CARD_TXN',
  recurring: '',
  recurrencePeriod: '',
}

export const validationSchema = yup.object({
  description: yup

    .string()
    .max(50, 'Description must be at most 50 characters.')
    .required('Category description is required and cannot be empty.'),

  displayText: yup.string(),

  shortDisplayText: yup.string(),

  eventType: yup.string().required('Event Type is required and cannot be empty.'),

  ledgerType: yup.string().required('Ledger Type is required and cannot be empty.'),

  displayType: yup.string(),

  ruleGroup: yup.string(),

  rulePriority: yup
    .string()
    .matches(/^(?:\d+|\d*\.\d+)$/, 'This field must be a valid number')
    .typeError('Rule Priority must be a numerical value.')
    .test('is-valid-number', 'This field must be a valid number', value => {
      const numberValue = parseFloat(value)
      return !isNaN(numberValue) && numberValue >= 0 && numberValue <= 100
    }),

  optIn: yup.boolean().required('Opt in is required'),

  singleTransaction: yup.boolean().required('Single Transaction is required'),

  daysAfterActivation: yup
    .string()
    .matches(/^(?:\d+|\d*\.\d+)$/, 'This field must be a valid number')
    .typeError('Days After Activation must be a numerical value.'),

  startDate: yup
    .date()
    .default(() => new Date())
    .required('Start date is required.'),

  endDate: yup
    .date()
    .nullable()
    .min(yup.ref('startDate'), 'End date cannot be before Start date')
    .test('end-date-validation', 'End date must be after Start date', function (value) {
      const { startDate } = this.parent
      const start = startDate || new Date()
      return !value || value >= start
    }),

  rewardClassList: yup.array().of(yup.string()),

  mccInclusionList: createListSchema('mccInclusionList'),

  mccExclusionList: createListSchema('mccExclusionList'),

  midInclusionList: createListSchema('midInclusionList'),

  midExclusionList: createListSchema('midExclusionList'),

  chainInclusionList: createListSchema('chainInclusionList'),

  chainExclusionList: createListSchema('chainExclusionList'),

  online: yup.string().oneOf(['online', 'in-store']),

  wallet: createListSchema('wallet'),

  conditions: yup.string(),

  minimumCount: yup
    .string()
    .matches(/^(?:\d+|\d*\.\d+)$/, 'This field must be a valid number')
    .typeError('Minimum Count must be a numerical value.'),

  minimumSpend: yup
    .string()
    .matches(/^(?:\d+|\d*\.\d+)$/, 'This field must be a valid number')
    .typeError('Minimum Spend must be a numerical value.'),

  maxCount: yup
    .string()
    .matches(/^(?:\d+|\d*\.\d+)$/, 'This field must be a valid number')
    .typeError('Maximum Count must be a numerical value.'),

  maxPoints: yup
    .string()
    .matches(/^(?:\d+|\d*\.\d+)$/, 'This field must be a valid number')
    .typeError('Maximum Points Amount must be a numerical value.'),

  fixedPoints: yup
    .string()
    .matches(/^(?:\d+|\d*\.\d+)$/, 'This field must be a valid number')
    .typeError('Fixed Points must be a numerical value.'),

  pointMultiplier: yup
    .string()
    .matches(/^(?:\d+|\d*\.\d+)$/, 'This field must be a valid number')
    .typeError('Point Multiplier must be a numerical value.'),

  pointsRequired: yup.boolean().typeError('Points Required must be a numerical value.'),

  displayScore: yup
    .string()
    .matches(/^(?:\d+|\d*\.\d+)$/, 'This field must be a valid number')
    .typeError('Fixed Points must be a numerical value.'),

  recurring: yup.string().oneOf(['Yes', 'No'], 'Invalid value for recurring'),
})
